

import { useEffect, useReducer, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import DataSetOperators from 'interfaces/DataSetOperators';
import Identifiable from 'interfaces/Identifiable';
import { ErrorCause } from 'models/ErrorResponse';

import DataSet from '../interfaces/DataSet';
import DataSetReducerMessage from '../interfaces/DataSetReducerMessage';
import { ApiService } from '../services/ApiService';

export const defaultTopNumberResults = 30;

export const useDataSet = <T extends Identifiable>(
  apiBaseUrl: string,
  apiEndpoint: string,
  projectId?: number,
  initialQueryParams?: DataSetOperators,
) => {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const scheduleService = new ApiService<T>(apiBaseUrl, apiEndpoint);
  let defaultOperators: DataSetOperators = {
    Top: defaultTopNumberResults,
  };
  if (projectId) {
    defaultOperators.ProjectId = projectId;
  }

  if (initialQueryParams) {
    defaultOperators = {
      ...defaultOperators,
      ...initialQueryParams,
    };
  }

  const [operators, setOperators] = useState<DataSetOperators>(defaultOperators);

  const defaultApiResponse: DataSet<T> = {
    responseData: [],
    isLoading: true,
    isError: false,
    error: undefined,
  };

  const apiResponseReducer = (
    _priorApiResponse: DataSet<T>,
    fieldUpdate: DataSetReducerMessage,
  ): DataSet<T> => {
    const updatedApiResponse: DataSet<T> = { ..._priorApiResponse };
    (updatedApiResponse as any)[fieldUpdate.type] = fieldUpdate.value;

    switch (fieldUpdate.type) {
      case 'responseData':
        updatedApiResponse.isLoading = false;
        updatedApiResponse.error = undefined;
        updatedApiResponse.isError = false;
        break;
      case 'error':
        updatedApiResponse.isError = true;
        updatedApiResponse.isLoading = false;
        break;
      default:
        break;
    }

    return updatedApiResponse;
  };

  const [dataSet, setDataSet] = useReducer(apiResponseReducer, defaultApiResponse);

  useEffect(() => {
    if (!isAuthenticated || isLoading) {
      return;
    }

    const fetchData = async () => {
      const token = await getAccessTokenSilently();
      if (!token) {
        setDataSet({ type: 'error', value: new Error('401 unauthorized', { cause: ErrorCause.AuthenticationError }) });
      } else {
        scheduleService.setAccessToken(token);
        setDataSet({ type: 'isLoading', value: true });
        const data = await scheduleService.all(operators);
        // keep this commented.  Seems to be the reason why all datasets load twice.
        // keeping line below for historical reasons in case in other areas of code it starts misbehaving.
        // setDataSet({ type: 'operators', value: operators });
        setDataSet({ type: 'responseData', value: data });
      }
    };

    fetchData().catch((e) => {
      setDataSet({ type: 'error', value: e });
    });
  }, [operators, isAuthenticated, isLoading]);

  return { dataSet, setOperators, operators };
};
